import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Card, Button } from '@heroui/react';
import { isChunkLoadError, handleChunkLoadError } from '../utils/chunk-error';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Otomatis reload jika error disebabkan oleh chunk lama yang hilang setelah deployment
    if (isChunkLoadError(error)) {
      handleChunkLoadError(error);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      const isChunk = isChunkLoadError(this.state.error);

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
          <Card className="max-w-md p-6 text-center">
            <h1 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
              {isChunk ? 'Pembaruan Tersedia' : 'Oops! Terjadi Kesalahan'}
            </h1>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              {isChunk
                ? 'Versi aplikasi telah diperbarui atau modul gagal dimuat. Silakan muat ulang halaman.'
                : 'Maaf, terjadi kesalahan yang tidak terduga pada aplikasi. Silakan refresh halaman.'}
            </p>
            {this.state.error && (
              <details className="mb-5 text-left">
                <summary className="cursor-pointer text-xs font-semibold text-rose-600 dark:text-rose-400">
                  Lihat detail kesalahan
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-[11px] font-mono text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200">
                  {this.state.error.message || String(this.state.error)}
                </pre>
              </details>
            )}
            <Button data-testid="global-error-boundary-refresh-page-btn" variant="primary" onPress={this.handleReload}>
              Refresh Halaman
            </Button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

